"use client";

import { tsv } from "@faire/slate/lib/tsv";
import { IPolimorphicProps } from "@faire/slate/private/shared/types";
import React from "react";

import { useGetMenuItemProps } from "./useGetMenuItemProps";

type IProps<P, T extends HTMLElement, E extends React.ElementType> = {
  interactive?: boolean;
  destructive?: boolean;
  ref?: React.Ref<T>;
  onClick?: (event: React.MouseEvent<T>) => void;
  className?: string;
} & IPolimorphicProps<E, P> &
  React.PropsWithChildren;

export const MenuItemBase = <
  P,
  T extends HTMLElement = HTMLButtonElement,
  E extends React.ElementType = "button",
>({
  children,
  as,
  interactive,
  destructive,
  className,
  ref,
  onClick,
  ...props
}: IProps<P, T, E>) => {
  const Component = as || (interactive ? "button" : "div");

  const { getProps } = useGetMenuItemProps<T>();

  const itemCls = itemStyles.cls({ interactive, destructive, className });
  const itemProps = interactive ? getProps({ ref, onClick }) : { ref };

  return (
    <li role="none" className="flex list-none flex-col">
      <Component {...props} {...itemProps} className={itemCls}>
        {children}
      </Component>
    </li>
  );
};

const itemStyles = tsv({
  base: "fs-menu__item",
  variants: {
    interactive: {
      true: "focusable interactive",
    },
    destructive: {
      true: "fs-menu__item--destructive",
    },
  },
});
